import { useEffect, useMemo, useState } from 'react';
import { alertDialog } from '../telegram';
import { fileOrderClaim, getOrder, getProducts } from '../api';
import type { ClaimIssue, ClaimItemInput, OrderDetail, Product } from '../types';

// ============================================================================
// Блок E (приёмка): форма «Есть расхождения» по отгруженному заказу
// (docs/deposits-spec.md раздел 14.2). Либо «заказ не доставлен целиком», либо
// по позициям: получено меньше / брак / пересорт (что получено вместо заказанного —
// блюдо из каталога или «иное»). Общий комментарий и до 5 фото (камера телефона).
// Фото по желанию: для брака и пересорта просим, но не блокируем.
// ============================================================================

interface Props {
  orderId: string;
  orderNumber: string;
  deadlineLabel?: string | null;
  onClose: () => void;
  // Вызывается после успешной подачи — родитель перечитывает список заказов.
  onFiled: () => void;
}

interface RowState {
  issue: '' | ClaimIssue;
  qty: string;
  receivedProductId: string;
  otherText: string;
  comment: string;
}

const MAX_PHOTOS = 5;

const ISSUE_OPTIONS: { value: '' | ClaimIssue; label: string }[] = [
  { value: '', label: 'Без расхождений' },
  { value: 'shortage', label: 'Получено меньше' },
  { value: 'defect', label: 'Брак' },
  { value: 'missort', label: 'Пересорт (не то блюдо)' },
];

const QTY_LABEL: Record<ClaimIssue, string> = {
  shortage: 'Не хватает, шт.',
  defect: 'Бракованных, шт.',
  missort: 'Не тех, шт.',
};

const btnPrimary: React.CSSProperties = {
  width: '100%', padding: '12px', border: 'none', borderRadius: 12,
  background: 'var(--tg-button)', color: 'var(--tg-button-text, #fff)',
  fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  ...btnPrimary, background: 'var(--tg-secondary-bg)', color: 'var(--tg-button)',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 10px', borderRadius: 10, fontSize: 14, fontFamily: 'inherit',
  border: '1px solid var(--tg-secondary-bg)', background: 'var(--tg-bg)', color: 'var(--tg-text)',
  boxSizing: 'border-box',
};
const toggleStyle = (active: boolean): React.CSSProperties => ({
  flex: 1, padding: '9px 8px', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
  background: active ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
  color: active ? 'var(--tg-button-text, #fff)' : 'var(--tg-text)',
});

export default function ClaimFormModal({ orderId, orderNumber, deadlineLabel, onClose, onFiled }: Props) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'items' | 'not_delivered'>('items');
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getOrder(orderId), getProducts().catch(() => ({ data: [] as Product[] }))])
      .then(([o, p]) => {
        if (cancelled) return;
        setOrder(o.data);
        setProducts(p.data);
        const init: Record<string, RowState> = {};
        for (const it of o.data.items) init[it.id] = { issue: '', qty: '1', receivedProductId: '', otherText: '', comment: '' };
        setRows(init);
      })
      .catch((e: Error) => { if (!cancelled) alertDialog(`Не удалось загрузить заказ: ${e.message}`); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  const setRow = (itemId: string, patch: Partial<RowState>) =>
    setRows(prev => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));

  const flagged = useMemo(
    () => (order?.items ?? []).filter(it => rows[it.id]?.issue),
    [order, rows],
  );

  const onPickPhotos = (files: FileList | null) => {
    if (!files) return;
    const next = [...photos, ...Array.from(files)].slice(0, MAX_PHOTOS);
    setPhotos(next);
  };

  const submit = async () => {
    if (!order || submitting) return;
    const items: ClaimItemInput[] = [];
    if (mode === 'items') {
      if (flagged.length === 0) { alertDialog('Отметьте хотя бы одну позицию с расхождением.'); return; }
      for (const it of flagged) {
        const r = rows[it.id];
        const qty = Number(r.qty);
        if (!Number.isInteger(qty) || qty < 1 || qty > it.quantity) {
          alertDialog(`«${it.product_name_snapshot}»: количество должно быть от 1 до ${it.quantity}.`);
          return;
        }
        if (r.issue === 'missort' && !r.receivedProductId && !r.otherText.trim()) {
          alertDialog(`«${it.product_name_snapshot}»: укажите, что получено вместо заказанного.`);
          return;
        }
        items.push({
          order_item_id: it.id,
          issue: r.issue as ClaimIssue,
          affected_qty: qty,
          received_product_id: r.issue === 'missort' && r.receivedProductId ? r.receivedProductId : null,
          received_other_text: r.issue === 'missort' && !r.receivedProductId ? r.otherText.trim() || null : null,
          comment: r.comment.trim() || null,
        });
      }
    }
    setSubmitting(true);
    try {
      await fileOrderClaim(orderId, { kind: mode, comment: comment.trim() || null, items }, photos);
      alertDialog('Претензия отправлена. Менеджер разберёт её и сообщит решение в Telegram.');
      onFiled();
    } catch (e) {
      alertDialog(`Не удалось отправить: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const needPhotoHint = mode === 'items' && flagged.some(it => rows[it.id].issue === 'defect' || rows[it.id].issue === 'missort');

  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, maxHeight: '92vh', overflowY: 'auto',
          background: 'var(--tg-bg)', color: 'var(--tg-text)', borderRadius: 16, padding: 18,
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Расхождения по заказу {orderNumber}</h3>
        {deadlineLabel && <p className="hint" style={{ margin: '0 0 12px' }}>Подать можно до {deadlineLabel}.</p>}

        {loading && <p className="hint">Загрузка…</p>}

        {!loading && order && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button style={toggleStyle(mode === 'items')} onClick={() => setMode('items')}>По позициям</button>
              <button style={toggleStyle(mode === 'not_delivered')} onClick={() => setMode('not_delivered')}>Не доставлен целиком</button>
            </div>

            {mode === 'not_delivered' && (
              <p className="hint" style={{ margin: '0 0 12px', lineHeight: 1.45 }}>
                Менеджер проверит доставку. Если подтвердится, вся сумма заказа вернётся на депозит.
              </p>
            )}

            {mode === 'items' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {order.items.map(it => {
                  const r = rows[it.id];
                  if (!r) return null;
                  return (
                    <div key={it.id} className="card" style={{ marginBottom: 0, padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{it.product_name_snapshot}</span>
                        <span className="hint" style={{ whiteSpace: 'nowrap' }}>× {it.quantity}</span>
                      </div>
                      <select
                        value={r.issue}
                        onChange={e => setRow(it.id, { issue: e.target.value as '' | ClaimIssue })}
                        style={{ ...inputStyle, marginTop: 8 }}
                      >
                        {ISSUE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {r.issue && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <label>
                            <span className="hint">{QTY_LABEL[r.issue]}</span>
                            <input
                              type="number" inputMode="numeric" min={1} max={it.quantity} value={r.qty}
                              onChange={e => setRow(it.id, { qty: e.target.value })}
                              style={{ ...inputStyle, marginTop: 4 }}
                            />
                          </label>
                          {r.issue === 'missort' && (
                            <>
                              <label>
                                <span className="hint">Что получено вместо заказанного</span>
                                <select
                                  value={r.receivedProductId}
                                  onChange={e => setRow(it.id, { receivedProductId: e.target.value })}
                                  style={{ ...inputStyle, marginTop: 4 }}
                                >
                                  <option value="">— иное (опишите ниже) —</option>
                                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                              </label>
                              {!r.receivedProductId && (
                                <input
                                  placeholder="Опишите, что получили"
                                  value={r.otherText}
                                  onChange={e => setRow(it.id, { otherText: e.target.value })}
                                  style={inputStyle}
                                />
                              )}
                            </>
                          )}
                          <input
                            placeholder="Комментарий к позиции (по желанию)"
                            value={r.comment}
                            onChange={e => setRow(it.id, { comment: e.target.value })}
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <label style={{ display: 'block', marginBottom: 10 }}>
              <span className="hint">Общий комментарий</span>
              <textarea
                rows={3} value={comment} onChange={e => setComment(e.target.value)}
                style={{ ...inputStyle, marginTop: 4, resize: 'vertical' }}
                placeholder="Что случилось, во сколько привезли, кто принимал…"
              />
            </label>

            <div style={{ marginBottom: 14 }}>
              <span className="hint">Фото (до {MAX_PHOTOS}){needPhotoHint ? ' — для брака и пересорта желательно приложить' : ''}</span>
              <input
                type="file" accept="image/*" multiple capture="environment"
                onChange={e => { onPickPhotos(e.target.files); e.target.value = ''; }}
                style={{ display: 'block', marginTop: 6, fontSize: 13 }}
                disabled={photos.length >= MAX_PHOTOS}
              />
              {photos.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                  {photos.map((f, i) => (
                    <div key={`${f.name}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span className="hint" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📷 {f.name || `фото ${i + 1}`}</span>
                      <button
                        onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                        style={{ background: 'transparent', border: 'none', color: '#ff3b30', fontFamily: 'inherit', cursor: 'pointer' }}
                      >
                        убрать
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={submit} disabled={submitting} style={{ ...btnPrimary, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Отправляем…' : '⚠️ Отправить претензию'}
            </button>
            <button onClick={onClose} disabled={submitting} style={{ ...btnSecondary, marginTop: 8 }}>
              Отмена
            </button>
            <p className="hint" style={{ margin: '10px 0 0', lineHeight: 1.4 }}>
              Компенсация — возвратом на депозит по решению менеджера. Допоставки нет: нужное можно заказать заново.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
